// src/components/GoalListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import GoalCard from './GoalCard';
import InitiativeCard from './InitiativeCard';
import CreateEntityModal from './CreateEntityModal';
import { GOAL_STATUS_OPTIONS, INITIATIVE_STATUS_OPTIONS } from '../constants'; 
import { usePersistedFilters, Tooltip, ToggleButton, SingleSelectDropdown } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { useEntityNavigationContext } from './EntityNavigation';
import { RiInformationLine, RiAddLine } from 'react-icons/ri';
import { useStaffContext } from '../contexts/StaffContext';


const GoalListView = () => {
    const { apiService, clientId, dateRange, setDateRange } = useOutletContext();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';

    const [goalsList, setGoalsList] = useState([]);
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [errorGoals, setErrorGoals] = useState(null);

    const [initiativesList, setInitiativesList] = useState([]);
    const [loadingInitiatives, setLoadingInitiatives] = useState(false);
    const [errorInitiatives, setErrorInitiatives] = useState(null);
    
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    // Goal status filter - persisted for card navigation
    const [statusFilter, setStatusFilter] = usePersistedFilters('statusFilter', '');
    // Initiative status filter (can be set from dashboard card)
    const [initiativeStatusFilter, setInitiativeStatusFilter] = usePersistedFilters('initiativeStatusFilter', '');
    // Show blocked initiatives filter (open initiatives with blocking issues)
    const [showBlockedInitiatives, setShowBlockedInitiatives] = usePersistedFilters('showBlockedInitiatives', false);

    // Create modal state
    const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
    const [showCreateInitiativeModal, setShowCreateInitiativeModal] = useState(false);

    const hasActiveFilters = selectedDris.length > 0 || statusFilter !== '' || initiativeStatusFilter !== '' || showBlockedInitiatives;
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setStatusFilter('');
        setInitiativeStatusFilter('');
        setShowBlockedInitiatives(false);
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !clientId) return;
        try {
            const staffResult = await apiService.getAllStaff(false);
            if (staffResult && staffResult.length > 0) {
                setDris(staffResult.map(s => s.staffName || s.name).filter(Boolean));
                return;
            }
            const driResult = await apiService.getClientDistinctDRIs(clientId);
            // Backend now returns normalized DRIs (staff names instead of emails)
            setDris(driResult.map(r => r.dri).filter(Boolean));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, clientId]);


    const loadGoalsList = useCallback(async () => {
        if (!apiService || !clientId || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingGoals(true); setErrorGoals(null);
        try {
            const d = await apiService.getAllGoalsList(
                clientId, 
                selectedDris.length > 0 ? selectedDris : null,
                statusFilter || null,
                dateRange.start,
                dateRange.end
            );
            setGoalsList(d);
        } catch (e) {
            setErrorGoals(e.message); setGoalsList([]);
        } finally { setLoadingGoals(false); }
    }, [apiService, clientId, selectedDris, statusFilter, dateRange]);

    const loadInitiativesList = useCallback(async () => {
         if (!apiService || !clientId || !dateRange || !dateRange.start || !dateRange.end) return;
         setLoadingInitiatives(true); setErrorInitiatives(null);
         try {
             // Don't pass 'blocked' to API - we'll filter locally for blocked initiatives
             // Only pass regular status filters to API
             const initFilter = (initiativeStatusFilter && initiativeStatusFilter !== 'blocked') 
                 ? initiativeStatusFilter 
                 : (statusFilter || null);
             const d = await apiService.getAllInitiativesList(
                 clientId, 
                 selectedDris.length > 0 ? selectedDris : null,
                 initFilter,
                 dateRange.start,
                 dateRange.end
             );
             setInitiativesList(d);
         } catch (e) {
             setErrorInitiatives(e.message); setInitiativesList([]);
         } finally { setLoadingInitiatives(false); }
    }, [apiService, clientId, selectedDris, statusFilter, initiativeStatusFilter, dateRange]);

    useEffect(() => {
        if (clientId) {
            loadDris();
        }
    }, [clientId, loadDris]);

    useEffect(() => {
        if (clientId && dateRange && dateRange.start && dateRange.end) {
            loadGoalsList();
            loadInitiativesList();
        }
    }, [clientId, selectedDris, statusFilter, initiativeStatusFilter, dateRange, loadGoalsList, loadInitiativesList]);

    const handleGoalUpdate = (itemId, updatedField, newValue) => {
        setGoalsList(prevList => 
            prevList.map(item => 
                item.id === itemId ? { ...item, [updatedField]: newValue } : item
            )
        );
    };

    const handleGoalStatusChange = async (goalId, newStatus) => {
        try {
            await apiService.updateNodeStatus(goalId, newStatus);
            handleGoalUpdate(goalId, 'status', newStatus);
        } catch (err) {
            console.error('Error updating goal status:', err);
        }
    };

    const handleGoalTargetDateChange = async (goalId, newDate) => {
        try {
            await apiService.updateNodeProperty(goalId, 'target_date', newDate);
            handleGoalUpdate(goalId, 'targetDate', newDate);
        } catch (err) {
            console.error('Error updating goal target date:', err);
        }
    };

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleInitiativeUpdate = (itemId, updatedField, newValue) => {
        setInitiativesList(prevList => 
            prevList.map(item => 
                item.id === itemId ? { ...item, [updatedField]: newValue } : item
            )
        );
    };

    const handleInitiativeStatusChange = async (initiativeId, newStatus) => {
        try {
            await apiService.updateNodeStatus(initiativeId, newStatus);
            handleInitiativeUpdate(initiativeId, 'status', newStatus);
        } catch (err) {
            console.error('Error updating initiative status:', err);
        }
    };

    const handleInitiativeTargetDateChange = async (initiativeId, newDate) => {
        try {
            await apiService.updateNodeProperty(initiativeId, 'target_date', newDate);
            handleInitiativeUpdate(initiativeId, 'targetDate', newDate);
            handleInitiativeUpdate(initiativeId, 'target_date', newDate);
        } catch (err) {
            console.error('Error updating initiative target date:', err);
        }
    };

    // Handle successful entity creation
    const handleGoalCreated = (newGoal) => {
        // Refresh the goals list
        loadGoalsList();
    };

    const handleInitiativeCreated = (newInitiative) => {
        // Refresh the initiatives list
        loadInitiativesList();
    };

    // Filter initiatives locally for "blocked" status
    // Blocked = open initiatives with blocking issues (blockingIssuesCount > 0)
    // Filter initiatives:
    // - showBlockedInitiatives: open initiatives with issues blocking them (blockingIssuesCount > 0)
    // - initiativeStatusFilter: filter by specific status
    const filteredInitiatives = useMemo(() => {
        if (showBlockedInitiatives) {
            // Show open initiatives that have blocking issues
            return initiativesList.filter(init => 
                init.status === 'open' && init.blockingIssuesCount > 0
            );
        }
        if (initiativeStatusFilter) {
            return initiativesList.filter(init => init.status === initiativeStatusFilter);
        }
        return initiativesList;
    }, [initiativesList, initiativeStatusFilter, showBlockedInitiatives]);

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[{ id: 'goals', label: 'Goals & Initiatives', filters: [{ key: 'goalStatus' }, { key: 'initiativeStatus' }, { key: 'blockedInitiatives' }] }]}
                filters={{ goalStatus: statusFilter, initiativeStatus: initiativeStatusFilter, blockedInitiatives: showBlockedInitiatives }}
                filterConfigs={{
                    goalStatus: { label: 'Goal Status', render: () => <SingleSelectDropdown id="statusFilter" label="Goal Status" value={statusFilter} onChange={setStatusFilter} options={GOAL_STATUS_OPTIONS} allLabel="All" /> },
                    initiativeStatus: { label: 'Initiative Status', render: () => <SingleSelectDropdown id="initiativeStatusFilter" label="Init. Status" value={initiativeStatusFilter} onChange={(val) => { setInitiativeStatusFilter(val); setShowBlockedInitiatives(false); }} options={INITIATIVE_STATUS_OPTIONS} allLabel="All" /> },
                    blockedInitiatives: { label: 'Blocked Initiatives', render: () => <Tooltip content="Shows open initiatives that have issues blocking them"><ToggleButton label="Blocked Init. Only" isActive={showBlockedInitiatives} onClick={() => { setShowBlockedInitiatives(!showBlockedInitiatives); if (!showBlockedInitiatives) setInitiativeStatusFilter(''); }} /></Tooltip> }
                }}
                activeFilterChips={[
                    ...(statusFilter ? [{ label: 'Goal', value: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1), onRemove: () => setStatusFilter('') }] : []),
                    ...(initiativeStatusFilter ? [{ label: 'Initiative', value: initiativeStatusFilter.charAt(0).toUpperCase() + initiativeStatusFilter.slice(1), onRemove: () => setInitiativeStatusFilter('') }] : []),
                    ...(showBlockedInitiatives ? [{ label: 'Blocked', value: 'Yes', onRemove: () => setShowBlockedInitiatives(false) }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />

            {/* 50/50 Split: Goals (left) + Initiatives (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Goals - Card View */}
                <div 
                    className="rounded-xl p-6"
                    style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                    data-testid="goal-list"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                                Goals
                            </h3>
                            <Tooltip content="Goals include newly created items without communications, plus items with communications in the selected date range.">
                                <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                            </Tooltip>
                        </div>
                        <button
                            onClick={() => setShowCreateGoalModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                            style={{ 
                                backgroundColor: 'var(--pallium-purple-0-5)',
                                color: 'var(--pallium-primary-purple)',
                            }}
                        >
                            <RiAddLine className="w-4 h-4" />
                            New Goal
                        </button>
                    </div>
                    {loadingGoals ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                            Loading goals...
                        </div>
                    ) : errorGoals ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>
                            Error loading goals: {errorGoals}
                        </div>
                    ) : goalsList.length > 0 ? (
                        <div className="space-y-3 max-h-[800px] overflow-y-auto">
                            {goalsList.map((goal, index) => (
                                <GoalCard
                                    key={goal.id || index}
                                    data-testid="goal-card"
                                    goal={goal}
                                    clientId={clientId}
                                    editable={true}
                                    statusOptions={GOAL_STATUS_OPTIONS}
                                    onStatusChange={handleGoalStatusChange}
                                    onTargetDateChange={handleGoalTargetDateChange}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                            No goals match the current filters.
                        </div>
                    )}
                </div>

                {/* Right: Initiatives - Card View */}
                <div 
                    className="rounded-xl p-6"
                    style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                    data-testid="initiative-list"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1.5">
                            <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                                {showBlockedInitiatives ? "Blocked Initiatives" : "Initiatives"}
                            </h3>
                            <Tooltip content="Initiatives include newly created items without communications, plus items with communications in the selected date range.">
                                <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                            </Tooltip>
                        </div>
                        <button
                            onClick={() => setShowCreateInitiativeModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                            style={{ 
                                backgroundColor: 'rgba(0, 156, 220, 0.1)',
                                color: 'var(--pallium-info-cyan)',
                            }}
                        >
                            <RiAddLine className="w-4 h-4" />
                            New Initiative
                        </button>
                    </div>
                    {loadingInitiatives ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                            Loading initiatives...
                        </div>
                    ) : errorInitiatives ? (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>
                            Error loading initiatives: {errorInitiatives}
                        </div>
                    ) : filteredInitiatives.length > 0 ? (
                        <div className="space-y-3 max-h-[800px] overflow-y-auto">
                            {filteredInitiatives.map((initiative, index) => (
                                <InitiativeCard
                                    key={initiative.id || index}
                                    data-testid="initiative-card"
                                    initiative={initiative}
                                    clientId={clientId}
                                    editable={true}
                                    statusOptions={INITIATIVE_STATUS_OPTIONS}
                                    onStatusChange={handleInitiativeStatusChange}
                                    onTargetDateChange={handleInitiativeTargetDateChange}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                            No initiatives match the current filters.
                        </div>
                    )}
                </div>
            </div>

            {/* Create Goal Modal */}
            <CreateEntityModal
                isOpen={showCreateGoalModal}
                onClose={() => setShowCreateGoalModal(false)}
                entityType="goal"
                clientId={clientId}
                apiService={apiService}
                onSuccess={handleGoalCreated}
                staffList={dris}
            />

            {/* Create Initiative Modal */}
            <CreateEntityModal
                isOpen={showCreateInitiativeModal}
                onClose={() => setShowCreateInitiativeModal(false)}
                entityType="initiative"
                clientId={clientId}
                apiService={apiService}
                onSuccess={handleInitiativeCreated}
                staffList={dris}
                goalsList={goalsList}
            />
        </>
    );
};

export default GoalListView;